import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {PermissionsService} from '../../../Services/permissions.service';
import {Subject} from 'rxjs';

/**
 * The FormHeaderButtonsComponent is responsible for rendering and managing the button actions
 * in the header of a form. This component facilitates the interaction between the user and the actions
 * typically associated with forms, such as saving, resetting, closing, emailing, and exporting data.
 * It provides various input properties for customization and outputs to emit user actions.
 *
 * Input Properties:
 * - `vm`: A data object or view model for form interaction.
 * - `ButtonConfig`: Configuration object to customize the visibility and configuration of buttons.
 * - `buttonsDisabled`: A global flag to enable/disable all buttons.
 * - `deleteButtonDisabled`: A flag to enable/disable the "Delete" button.
 * - `saveButtonDisabled`: A flag to enable/disable the "Save" button.
 * - `saveCloseButtonDisabled`: A flag to enable/disable the "Save and Close" button.
 * - `saveNewButtonDisabled`: A flag to enable/disable the "Save and New" button.
 * - `resetButtonDisabled`: A flag to enable/disable the "Reset" button.
 * - `printButtonDisabled`: A flag to enable/disable the "Print" button.
 * - `emailButtonDisabled`: A flag to enable/disable the "Email" button.
 * - `exportButtonDisabled`: A flag to enable/disable the "Export" button.
 * - `Disabled`: A configurable property to handle custom button states.
 * - `emailObj`: An object containing email-specific details.
 *
 * Output Events:
 * - `childSave`: Emits an event when the "Save" action is triggered.
 * - `childSaveClose`: Emits an event when the "Save and Close" action is triggered.
 * - `childSaveNew`: Emits an event when the "Save and New" action is triggered.
 * - `childDeleteItem`: Emits an event when the "Delete" action is triggered.
 * - `childResetForm`: Emits an event when the "Reset" action is triggered.
 * - `childClose`: Emits an event when the "Close" action is triggered.
 * - `childEmail`: Emits an event when the "Email" action is triggered.
 * - `childPrint`: Emits an event when the "Print" action is triggered.
 * - `childExport`: Emits an event when the "Export" action is triggered.
 * - `childDelivered`: Emits an event when the "Delivered" action is triggered.
 * - `childLocked`: Emits an event when the "Locked" action is triggered.
 *
 * Methods:
 * - `close()`: Triggers the "Close" action and emits `childClose`.
 * - `resetForm()`: Triggers the "Reset Form" action and emits `childResetForm`.
 * - `deleteItem()`: Triggers the "Delete Item" action and emits `childDeleteItem`.
 * - `saveNew()`: Triggers the "Save New" action and emits `childSaveNew`.
 * - `saveClose()`: Triggers the "Save Close" action and emits `childSaveClose`.
 * - `save()`: Triggers the "Save" action and emits `childSave`.
 * - `print()`: Triggers the "Print" action and emits `childPrint`.
 * - `export()`: Triggers the "Export" action and emits `childExport`.
 * - `email()`: Triggers the "Email" action and emits `childEmail`.
 * - `delivered()`: Triggers the "Delivered" action and emits `childDelivered`.
 * - `locked()`: Triggers the "Locked" action and emits `childLocked`.
 */
@Component({
  selector: 'app-form-header-buttons',
  templateUrl: './form-header-buttons.component.html',
  styleUrls: ['./form-header-buttons.component.css'],
  standalone: false
})
export class FormHeaderButtonsComponent implements OnInit {

  @Input() vm: any;
  @Input() ButtonConfig: any;
  @Input() buttonsDisabled: boolean;
  @Input() deleteButtonDisabled: boolean;
  @Input() saveButtonDisabled: boolean;
  @Input() saveCloseButtonDisabled: boolean;
  @Input() saveNewButtonDisabled: boolean;
  @Input() cloneButtonDisabled: boolean;
  @Input() resetButtonDisabled: boolean;

  @Input() printButtonDisabled: boolean;
  @Input() emailButtonDisabled: boolean;
  @Input() exportButtonDisabled: boolean;
  @Input() Disabled: any;
  @Input() emailObj: any;
  @Input() permissionKey?: string;

  @Output() childSave = new EventEmitter();
  @Output() childSaveClose = new EventEmitter();
  @Output() childSaveNew = new EventEmitter();
  @Output() childClone = new EventEmitter();
  @Output() childDeleteItem = new EventEmitter();
  @Output() childResetForm = new EventEmitter();
  @Output() childClose = new EventEmitter<true>();
  @Output() childEmail = new EventEmitter();
  @Output() childPrint = new EventEmitter();
  @Output() childExport = new EventEmitter();
  @Output() childDelivered = new EventEmitter();
  @Output() childLocked = new EventEmitter();

  clickEvent = new Subject();

  Buttons: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    clone: false,
    email: false,
    print: false,
    export: false,
    delivered: false,
    locked: false
  };

  get hasEditPermission(): boolean {
    if (!this.permissionKey) {
      return true;
    }
    if (this.permService.Get('Admin')) {
      return true;
    }
    return this.permService.Get(this.permissionKey);
  }

  constructor(private permService: PermissionsService) {
  }

  ngOnInit() {
    if (this.ButtonConfig) {
      this.Buttons = this.ButtonConfig;
    }
  }

  close() {
    this.childClose.emit(true);
  }

  resetForm() {
    this.childResetForm.emit(true);
  }

  deleteItem() {
    this.childDeleteItem.emit(true);
  }

  saveNew() {
    this.childSaveNew.emit(true);
  }

  clone() {
    this.childClone.emit(true);
  }

  saveClose() {
    this.childSaveClose.emit(true);
  }

  save() {
    this.childSave.emit(true);
  }

  print() {
    this.childPrint.emit(true);
  }

  export() {
    this.childExport.emit(true);
  }

  email() {
    return this.childEmail.emit(true);
  }

  delivered(){
    this.childDelivered.emit(true);
  }

  locked(){
    this.childLocked.emit(true);
  }
}
